import { config } from '@services/config';
import { SystemAlertSearchRequestQuery, SystemAlertSearchResponse } from '@lio/api-types';
import { alertsQueryKeys } from '@services/alert/keys';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

const searchAlertsQueryParams = ({
  pageLimit,
  pageOffset,
  type,
  activeAt,
  startAfter,
  endBefore,
  endAfter,
  startBefore,
  sort,
  showToUnauthorizedUsers,
}: SystemAlertSearchRequestQuery) => {
  const queryString = new URLSearchParams();
  const queryParams: Partial<SystemAlertSearchRequestQuery> = {};

  if (sort) {
    queryParams.sort = sort;
    queryString.append('sort', sort);
  }

  if (pageLimit) {
    queryParams.pageLimit = pageLimit;
    queryString.append('pageLimit', String(pageLimit));
  }
  if (pageOffset) {
    queryParams.pageOffset = pageOffset;
    queryString.append('pageOffset', String(pageOffset));
  }
  if (type) {
    queryParams.type = type;
    queryString.append('type', type);
  }
  if (activeAt) {
    if (activeAt === 'now') {
      queryParams.activeAt = 'now';
      queryString.append('activeAt', new Date().toISOString());
    } else {
      queryParams.activeAt = activeAt;
      queryString.append('activeAt', queryParams.activeAt);
    }
  }

  if (endAfter) {
    if (endAfter === 'now') {
      queryParams.endAfter = 'now';
      queryString.append('endAfter', new Date().toISOString());
    } else {
      queryParams.endAfter = endAfter;
      queryString.append('endAfter', endAfter);
    }
  }

  if (startBefore) {
    queryParams.endAfter = startBefore;
    queryString.append('startBefore', startBefore);
  }

  if (startAfter) {
    queryParams.startAfter = startAfter;
    queryString.append('startAfter', startAfter);
  }
  if (endBefore) {
    queryParams.endBefore = endBefore;
    queryString.append('endBefore', endBefore);
  }

  if (showToUnauthorizedUsers !== undefined) {
    queryParams.showToUnauthorizedUsers = showToUnauthorizedUsers;
    queryString.append('showToUnauthorizedUsers', showToUnauthorizedUsers ? 'true' : 'false');
  }

  return {
    queryParams,
    queryString: queryString.toString(),
  };
};

export const useSearchAlerts = (params: SystemAlertSearchRequestQuery, enabled: boolean = false) => {
  const client = useClient();

  const { queryParams, queryString } = searchAlertsQueryParams(params);

  const result = useQuery<SystemAlertSearchResponse, Error>({
    queryKey: [...alertsQueryKeys.searchWithParams(queryParams)],
    ...config,
    gcTime: 15000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const alerts = await client
        .get(`system-management/system-alert?${queryString}`)
        .json<SystemAlertSearchResponse>();
      return alerts;
    },
    enabled,
  });

  return {
    ...result,
    alertList: result.data,
  };
};
